/**
 * Duplicate Actions - Snippet duplication with UI integration
 *
 * This action handles snippet duplication by creating a new snippet with the same
 * content (name, body, description from the duplicate modal) via the create API.
 * - Creates the new snippet via createSnippet
 * - Shows success/error flags (duplicate-specific wording)
 * - Adds the new snippet to the editor's quick insert menu
 * - Refreshes the snippets list
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { messages } from '../../modals/SnippetEditorModal.i18n.ts';
import { addSnippetQuickInsertItem } from '../../quick-insert/addSnippetQuickInsertItem.tsx';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import type { CreateSnippetPayload } from '../../services/blockServiceTypes.ts';
import { clearSnippetsCache } from '../../services/snippetsCache.ts';
import { SnippetBodyFormat, SnippetProduct } from '../../snippetTypes.ts';
import type { Snippet } from '../../snippetTypes.ts';
import { reportError } from '../../reportError.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import { showSnippetErrorFlag, showSnippetSuccessFlag } from './showSnippetFlags.ts';

/**
 * Options for duplicateSnippet that enable UI feedback and editor integration
 */
export type DuplicateSnippetOptions = {
	/** Flags API for showing success/error notifications */
	flags: WithFlagsProps['flags'];
	/** Intl for message formatting */
	intl: IntlShape;
	/** Editor API for adding snippet to quick insert menu after duplication */
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
};

/**
 * Actions for duplicating snippets with UI integration.
 */
export const duplicateActions = {
	/**
	 * Duplicates a snippet and refreshes the snippets list.
	 *
	 * After successful duplication:
	 * - Clears the service cache and fetches the updated list
	 * - Shows a success flag (if options provided)
	 * - Adds the new snippet to the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Returns null (does not throw when options provided)
	 *
	 * @param name - The name for the duplicated snippet
	 * @param body - The ADF content body
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, and editor API ref
	 * @returns Promise resolving to the duplicated snippet, or null on failure
	 */
	duplicateSnippet:
		(name: string, body: ADFEntity, description?: string, options?: DuplicateSnippetOptions) =>
		async ({ setState }: StoreActionApi<SnippetsState>): Promise<Snippet | null> => {
			const descriptionValue = description?.trim();
			const params: CreateSnippetPayload = {
				name,
				...(descriptionValue && { description: descriptionValue }),
				body: JSON.stringify(body),
				bodyFormat: SnippetBodyFormat.ADF,
				product: SnippetProduct.CONFLUENCE,
			};

			setState({ isLoading: true });

			try {
				const snippet = await BlockServiceClient.createSnippet(params);

				clearSnippetsCache();
				const updatedSnippets = await BlockServiceClient.fetchSnippets();

				setState({
					snippets: updatedSnippets,
					isLoading: false,
					isLoaded: true,
				});

				if (options) {
					const { flags, intl, editorAPI } = options;
					showSnippetSuccessFlag(
						flags,
						intl.formatMessage(messages.snippetEditorModalDuplicateSuccessFlagTitle, {
							snippetName: snippet.name,
						}),
						intl.formatMessage(messages.snippetEditorModalDuplicateSuccessFlagDescription),
					);
					if (editorAPI) {
						addSnippetQuickInsertItem({ snippet, editorAPI });
					}
				}
				return snippet;
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));

				setState({ isLoading: false });
				reportError(err);

				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(messages.snippetEditorModalDuplicateErrorFlagTitle),
						options.intl.formatMessage(messages.snippetEditorModalDuplicateErrorFlagDescription),
					);
					return null;
				}
				throw err;
			}
		},
};
